import { C_R } from '@fielded/fs-api/lib/shipment/tools/planning-types'
import { byId } from '../../../subapps/shipments/common/utils'
import { isRmsDriver } from '../../../van-shared/utils/utils'

const reducerName = 'shipments'
const UPDATE_SHIPMENT = `${reducerName}/UPDATE_SHIPMENT`
const RECEIVE_SHIPMENTS = `${reducerName}/RECEIVE_SHIPMENTS`

const updateShipmentReducer = (state, action) => {
  const parentDocId = action.shipment.history[action.shipment.snapshotId].parentDocId
  let nextState = {...state}
  if (parentDocId) {
    const { [parentDocId]: omit, ...rest } = state
    nextState = rest
  }
  return {
    ...nextState,
    [action.shipment.snapshotId]: {
      ...action.shipment
    }
  }
}

const receiveShipments = (state, action) => ({
  ...byId(action.shipments, 'snapshotId')
})

export default (state = {}, action) => {
  switch (action.type) {
    case UPDATE_SHIPMENT: return updateShipmentReducer(state, action)
    case RECEIVE_SHIPMENTS: return receiveShipments(state, action)
    default: return state
  }
}

export const selectShipments = (state) => state.shipments

export const updateShipment = shipment => ({
  type: UPDATE_SHIPMENT,
  shipment
})

export const findShipments = (location, locationIsGeoLocation, hideNewShipmentsIfRouteSpecificUser, options) => async (dispatch, getState, { user, api }) => {
  // For PSM users asides from drivers and facility users get their shipments from postgress and do not need any extra computation here
  if (options && options.shouldUsePGShipments) {
    const { shipments } = await api.shipment.listLastMileDeliveries()
    return dispatch({type: RECEIVE_SHIPMENTS, shipments})
  }

  // This is kind of online-offline if you're calling it for a location that is not yours
  // but VAN does not expose that and SL does not use online offline
  // so there is no need to handle online-offline errors at the moment

  // By submitting a start date, the api will take the online path
  // for online-offline users, to load the entire history rather than
  // only what's offline available
  const startdate = (options && options.online) ? options.startdate || '2018-01-01' : null
  const promises = [
    api.shipment.find({ location, locationIsGeoLocation, startdate }, options)
  ]
  // If the location is a geo location we need to filter the shipments to only
  // include those that are valid locations for the users geo location. This way
  // we exclude shipments for locations that the user does not have the funder.
  // For that we need to get the locations the user can see.
  if (locationIsGeoLocation) {
    promises.push(
      api.location.listChildren(location.id, {includeSelf: true})
    )
  }
  return Promise.all(promises)
    .then(([shipments, locations]) => {
      // PSM Drivers and carriers are national users with specific routes
      // We filter here so we don't get external shipments
      if (options && options.noExternalShipments) {
        shipments = shipments.filter(s => !s.id.startsWith('origin:country:'))
      }

      const isDriver = user && user.roles && user.roles.some(role => role.includes('userRole:psm-driver'))
      const driverRouteIds = (user && user.routes) || []
      const driverRouteIdSet = new Set(driverRouteIds)

      // CRITICAL FIX: Drivers should ONLY see shipments assigned to their routes.
      // Filter out shipments with routeId = null (RMS shipments) for drivers.
      if (isDriver && driverRouteIds.length > 0) {
        shipments = shipments.filter(s => s.routeId && driverRouteIdSet.has(s.routeId))
      } else if (isDriver && driverRouteIds.length === 0) {
        // Driver with no routes should see NO shipments
        shipments = []
      }

      if (locationIsGeoLocation) {
        const locationIdSet = new Set(locations.map(l => l._id))
        if (locationIdSet.size === 0 && location && location.id) {
          // Fallback to user's own location when children list is empty
          locationIdSet.add(location.id)
        }

        // RMS drivers get shipments via route-based sync, not location-based.
        // Their shipments may originate from different warehouses, so skip location filtering.
        const userIsRmsDriver = isRmsDriver(user)

        shipments = shipments.filter(s => {
          // RMS drivers: trust the route-based sync, keep all shipments with matching routes
          if (userIsRmsDriver && s.routeId && driverRouteIdSet.has(s.routeId)) {
            return true
          }

          const originId = s.origin && s.origin.id
          const destinationId = s.destination && s.destination.id
          const hasLocationMatch = (originId && locationIdSet.has(originId)) || (destinationId && locationIdSet.has(destinationId))

          // If shipment came via route-scoped offline sync but lacks location objects,
          // keep it (routeId implies relevance for a driver/carrier).
          if (!originId && !destinationId && s.routeId) return true

          return hasLocationMatch
        })

        // This is a ShelfLife driver (i.e. a "route specific user").
        // This filter here is not ideal, but the whole design around snapshots and the API
        // might change soon. Why not let api.shipment.find filter the shipments that are
        // relevant to the particular user?
        const identifier = user.routes ? user.routes.length : user.funders.length
        if (hideNewShipmentsIfRouteSpecificUser && identifier) {
          shipments = shipments.filter(s => {
            if (s.isAutomaticReturnShipment) {
              return ['new', 'sent', 'received'].includes(s.status)
            }
            const notNew = s.status !== 'new'
            const isAutomaticReturn = s.isAutomaticReturnShipment && s.status === 'new'
            const createdBy = s.createdBy.user === user.name
            const isCR = s.planningType === C_R && s.status === 'new'
            return notNew || createdBy || isCR || isAutomaticReturn
          })
        }
      } else {
        const destinedShipments = shipments.filter(shipment => shipment.destination.id === location.id && shipment.status !== 'new')
        const originShipments = shipments.filter(shipment => shipment.origin.id === location.id)
        shipments = [...originShipments, ...destinedShipments]
      }

      dispatch({type: RECEIVE_SHIPMENTS, shipments})
    })
    .catch(err => {
      console.error('shipment loading error', err)
      throw err
    })
}
